/**
 * AppShell — sidebar + topbar, dựng lại đúng markup của prototype
 * (`assets/app.js` renderSidebar / renderTopbar) nên dùng chung app.css.
 *
 * Khác prototype ở hai điểm có chủ đích:
 *  - Bỏ công tắc "Xem với vai trò Admin/User": docs/00 nói rõ đó chỉ là công cụ
 *    xem nhanh của prototype, không phải tính năng sản phẩm.
 *  - Mục sidebar hiện theo capability thật (docs/05 §5.6), không theo vai trò cứng.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Icon } from '../components/Icon';
import { GlobalSearch } from '../components/GlobalSearch';
import { ImportProgress } from '../components/ImportProgress';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n, useT } from '../lib/i18n';
import { THEME_MODES, useTheme } from '../lib/theme';
import { avatarTone, initialsOf, useFormat } from '../lib/format';
import type { DashboardSummary } from '../lib/types';
import { useNhanBreadcrumb } from './breadcrumb';

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

interface NotificationList {
  items: NotificationItem[];
  unreadCount: number;
}

interface NavItem {
  id: string;
  to: string;
  labelKey: string;
  icon: string;
  /** Điều kiện hiển thị — docs/05 §5.6. `undefined` nghĩa là luôn hiện. */
  requires?: string[];
}

const NAV_GROUPS: { key: string; items: NavItem[] }[] = [
  {
    key: 'nav.group.operate',
    items: [
      { id: 'overview', to: '/', labelKey: 'nav.overview', icon: 'overview' },
      {
        id: 'domains',
        to: '/domains',
        labelKey: 'nav.domains',
        icon: 'domains',
        requires: ['dom.view'],
      },
      /*
       * OQ-291 — KHÔNG có 
equires.
       *
       * Người chỉ được chia sẻ một sub-domain không có dom.view (đó là quyền
       * hệ thống, gắn với vai trò), nên đặt điều kiện ở đây sẽ giấu mất lối vào
       * duy nhất của họ. Phạm vi do máy chủ thu hẹp; danh sách rỗng thì trang
       * tự nói ra bằng EmptyState.
       */
      { id: 'subdomains', to: '/subdomains', labelKey: 'nav.subdomains', icon: 'dns' },
      /*
       * KHÔNG có mục *DNS records* ở đây — lệch FR-001 có chủ ý (OQ-118).
       *
       * FR-001 liệt kê nó trong nhóm "Làm việc", và M7 đã dựng nó thành một
       * mục chuyển hướng về `/domains` (OQ-095). Chủ dự án dùng thật rồi bỏ:
       * *"nút DNS records không hữu ích lắm vì chúng ta đã có tab DNS records
       * của từng tên miền rồi"*.
       *
       * Lý do nó có mặt trong prototype: `assets/app.js` là một bộ 11 trang
       * TĨNH, mỗi trang đứng riêng, nên thanh điều hướng phải có lối vào
       * `domain-detail.html` để người xem bấm tới được. Trong SPA, chi tiết
       * tên miền đến từ danh sách. Mục ấy là **cấu trúc của bản mẫu**, không
       * phải yêu cầu sản phẩm — mà FR-001 chép lại bản mẫu.
       *
       * Route `/dns` GIỮ NGUYÊN (xem `routes.tsx`) để dấu trang cũ không gãy.
       */
    ],
  },
  {
    key: 'nav.group.admin',
    items: [
      {
        id: 'users',
        to: '/admin/users',
        labelKey: 'nav.users',
        icon: 'users',
        requires: ['adm.users'],
      },
      {
        id: 'roles',
        to: '/admin/roles',
        labelKey: 'nav.roles',
        icon: 'roles',
        requires: ['adm.roles'],
      },
      {
        id: 'domain-roles',
        to: '/admin/domain-roles',
        labelKey: 'nav.domainRoles',
        icon: 'badge',
        requires: ['adm.domainroles'],
      },
      { id: 'permissions', to: '/permissions', labelKey: 'nav.permissions', icon: 'shield' },
      {
        id: 'connections',
        to: '/connections',
        labelKey: 'nav.connections',
        icon: 'cloud',
        requires: ['conn.own', 'conn.all'],
      },
      {
        id: 'settings',
        to: '/admin/settings',
        labelKey: 'nav.settings',
        icon: 'settings',
        requires: ['adm.settings'],
      },
    ],
  },
  {
    key: 'nav.group.account',
    items: [
      { id: 'profile', to: '/profile', labelKey: 'nav.profile', icon: 'profile' },
      { id: 'audit', to: '/audit-log', labelKey: 'nav.audit', icon: 'audit' },
    ],
  },
];

/** Tiêu đề trang cho breadcrumb — khớp `DNSHub.boot({ title })` của prototype. */
const PAGE_TITLES: { match: (path: string) => boolean; key: string }[] = [
  { match: (p) => p === '/', key: 'nav.overview' },
  { match: (p) => p.startsWith('/profile'), key: 'nav.profile' },
  { match: (p) => p.startsWith('/admin/roles'), key: 'nav.roles' },
  { match: (p) => p.startsWith('/admin/domain-roles'), key: 'nav.domainRoles' },
  { match: (p) => p.startsWith('/admin/users'), key: 'nav.users' },
  { match: (p) => p.startsWith('/admin/settings'), key: 'nav.settings' },
  { match: (p) => p.startsWith('/subdomains'), key: 'nav.subdomains' },
  { match: (p) => p.startsWith('/domains'), key: 'nav.domains' },
  { match: (p) => p.startsWith('/dns'), key: 'nav.dns' },
  { match: (p) => p.startsWith('/permissions'), key: 'nav.permissions' },
  { match: (p) => p.startsWith('/connections'), key: 'nav.connections' },
  { match: (p) => p.startsWith('/audit-log'), key: 'nav.audit' },
];

export function AppShell({ children }: { children: React.ReactNode }) {
  const { formatNumber } = useFormat();
  const t = useT();
  const { locale, setLocale } = useI18n();
  const { user, can, logout, branding } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [navOpen, setNavOpen] = useState(false);

  /*
   * OQ-336 — đẩy múi giờ hồ sơ xuống `ThemeProvider`.
   *
   * `ThemeProvider` phải bọc cả màn đăng nhập nên nó nằm NGOÀI `AuthProvider` và
   * không tự đọc được hồ sơ. `AppShell` là nơi đầu tiên có cả hai, nên nó làm cầu.
   *
   * Người dùng đổi múi giờ trong hồ sơ thì màu tự chỉnh theo ngay, không cần tải
   * lại trang — `user.timezone` đổi là effect này chạy lại.
   */
  const { datMuiGio } = useTheme();
  useEffect(() => {
    datMuiGio(user?.timezone);
  }, [user?.timezone, datMuiGio]);

  /*
   * Nhãn suy từ đường dẫn là mặc định; trang nào cần nhãn riêng thì tự đặt
   * (`datNhanBreadcrumb`). `/domains/:id` là trường hợp duy nhất hiện nay —
   * prototype đòi breadcrumb đọc ra TÊN VÙNG, không phải "Tên miền" (TC-14).
   */
  const nhanRieng = useNhanBreadcrumb();
  const pageTitle =
    nhanRieng ?? t(PAGE_TITLES.find((p) => p.match(location.pathname))?.key ?? 'app.name');

  // Ngăn kéo điều hướng ở <900px dùng `body[data-nav='open']` như prototype.
  useEffect(() => {
    if (navOpen) document.body.setAttribute('data-nav', 'open');
    else document.body.removeAttribute('data-nav');
    return () => document.body.removeAttribute('data-nav');
  }, [navOpen]);

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setNavOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navOpen]);

  // Đóng ngăn kéo khi đổi trang.
  useEffect(() => setNavOpen(false), [location.pathname]);

  const visible = (item: NavItem) => !item.requires || item.requires.some((cap) => can(cap));

  /**
   * FR-003 — bộ đếm bên phải mục sidebar **lấy từ API, không hard-code**.
   *
   * Dùng đúng khoá truy vấn của trang Tổng quan nên react-query gộp làm một lời
   * gọi; sidebar không sinh thêm lượt mạng nào. Chưa có dữ liệu thì **không
   * hiện gì** thay vì hiện `0` — số 0 giả còn tệ hơn không có số
   * (`docs/11 §11.6`).
   */
  const summaryQuery = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: () => api.get<DashboardSummary>('/dashboard/summary'),
    enabled: Boolean(user),
  });

  const navCount = (id: string): number | null => {
    const counts = summaryQuery.data?.navCounts;
    if (!counts) return null;
    if (id === 'domains') return counts.domains;
    if (id === 'users') return counts.users;
    if (id === 'connections') return counts.connections;
    if (id === 'subdomains') return counts.subdomains;
    return null;
  };

  const initials = user ? initialsOf(user.fullName, user.email) : '··';
  const tone = user ? avatarTone(user.id) : 'tone-a';

  return (
    <div className="app" data-shell>
      <aside className="sidebar" data-od-id="app-sidebar">
        <div className="brand-row">
          <Link className="brand" to="/" data-od-id="brand">
            <span className="brand-mark">
              <Icon name="domains" size={18} />
            </span>
            <span>
              {/*
               * OQ-290 — hai dòng này từng là một khoá i18n và một chuỗi CỨNG.
               *
               * Cùng một vị trí, cùng một vai trò, mà dòng trên đổi theo ngôn
               * ngữ còn dòng dưới thì không. Màn đăng nhập ở AuthLayout lại
               * dùng 	('app.tagline') cho đúng chỗ ấy — nên hai màn hình nói
               * hai thứ khác nhau về cùng một hệ thống.
               *
               * Nay: Admin đặt gì thì hiện nấy; để trống thì rơi về bản dịch.
               */}
              <span className="brand-name">{branding.appName ?? t('app.name')}</span>
              <br />
              <span className="brand-sub">{branding.tagline ?? t('app.tagline')}</span>
            </span>
          </Link>
          {/*
           * Nút đóng — OQ-124. Ngăn kéo mở ra mà không có đường thoát nào
           * nhìn thấy được: không nút đóng, và lớp phủ là phần tử giả nên bấm
           * ra ngoài cũng không ăn. Chỉ còn phím Esc, thứ không ai đoán ra
           * trên điện thoại.
           */}
          <button
            className="icon-btn sidebar-close"
            type="button"
            aria-label={t('nav.close')}
            onClick={() => setNavOpen(false)}
            data-od-id="nav-close"
          >
            <Icon name="close" />
          </button>
        </div>

        {NAV_GROUPS.map((group) => {
          const items = group.items.filter(visible);
          if (items.length === 0) return null;
          return (
            <nav className="nav-group" key={group.key}>
              <span className="eyebrow">{t(group.key)}</span>
              {items.map((item) => {
                const count = navCount(item.id);
                return (
                  <NavLink
                    key={item.id}
                    className="nav-item"
                    to={item.to}
                    end={item.to === '/'}
                    data-od-id={`nav-${item.id}`}
                  >
                    <Icon name={item.icon} />
                    <span>{t(item.labelKey)}</span>
                    {count === null ? null : (
                      <span className="nav-count" data-od-id={`nav-count-${item.id}`}>
                        {formatNumber(count)}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </nav>
          );
        })}

        <div className="sidebar-foot">
          <div className="role-switch">
            <span className="eyebrow">{t('profile.info.locale')}</span>
            <div className="seg">
              <button type="button" aria-pressed={locale === 'vi'} onClick={() => setLocale('vi')}>
                Tiếng Việt
              </button>
              <button type="button" aria-pressed={locale === 'en'} onClick={() => setLocale('en')}>
                English
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/*
       * Lớp phủ THẬT — OQ-124.
       *
       * `aria-hidden` vì nó không mang thông tin: đường thoát dành cho bàn
       * phím và trình đọc màn hình là nút đóng ở trên và phím Esc. Nếu để nó
       * nhận tiêu điểm thì người dùng bàn phím phải Tab qua một ô trống rỗng
       * không có nhãn.
       */}
      {navOpen ? (
        <div className="nav-scrim" aria-hidden="true" onClick={() => setNavOpen(false)} />
      ) : null}

      <div className="main">
        <header className="topbar" data-od-id="app-topbar">
          <button
            className="icon-btn sidebar-toggle"
            type="button"
            aria-label={t('nav.open')}
            aria-expanded={navOpen}
            onClick={() => setNavOpen((open) => !open)}
          >
            <Icon name="menu" />
          </button>

          <div className="crumbs">
            <span className="trail">
              <Link to="/">{branding.appName ?? t('app.name')}</Link>
              <span className="sep">/</span>
            </span>
            <strong>{pageTitle}</strong>
          </div>

          <span className="topbar-spacer" />

          {/* OQ-282 — tiến độ nhập tên miền, hiện trên mọi trang khi còn chạy. */}
          <ImportProgress />

          {/* FR-005 — tìm kiếm toàn cục, phím tắt `/` (đóng OQ-013). */}
          <GlobalSearch />

          <ThemeMenu />

          {/* Thông báo trong ứng dụng — GET /notifications (docs/04 §4.12). */}
          <NotificationBell />

          <Link className="user-chip" to="/profile" data-od-id="account-chip">
            <span className={`avatar ${tone}`}>{initials}</span>
            <span className="who">
              <b>{user?.fullName || user?.email}</b>
              <span>{user?.role.name}</span>
            </span>
          </Link>

          <button
            className="icon-btn"
            type="button"
            aria-label={t('nav.logout')}
            onClick={async () => {
              await logout();
              navigate('/login', { replace: true });
            }}
            data-od-id="logout"
          >
            <Icon name="logout" />
          </button>
        </header>

        {/*
         * KHÔNG còn `onClick` đóng ngăn kéo ở đây.
         *
         * Bản trước gắn `onClick={() => navOpen && setNavOpen(false)}` lên
         * chính thẻ này, và nó **chưa từng chạy**: lớp phủ `body::after` phủ
         * lên trên nên đích của mọi cú bấm là `<body>`, không phải `.content`.
         * Một lưới an toàn không bao giờ đỡ được ai, mà đọc lên thì tưởng là
         * đã có xử lý — nên nó khiến lỗi OQ-124 sống lâu hơn.
         *
         * Nay `.nav-scrim` là phần tử thật và tự lo việc đóng.
         */}
        <div className="content">{children}</div>
      </div>
    </div>
  );
}

/**
 * Chuông thông báo — `GET /notifications` và `POST /notifications/read`
 * (docs/04 §4.12). Mốc M0–M2 khoá nút này vì chưa có API (OQ-013).
 */
/**
 * Chọn chế độ giao diện — sáng · tối · tự động (OQ-288).
 *
 * ── Vì sao MENU chứ không phải nút xoay vòng ──────────────────────────────
 *
 * Với hai trạng thái, một nút bấm-để-đổi là đủ: bấm xong thấy ngay kết quả và
 * bấm lại là về chỗ cũ. Với ba trạng thái thì không — người dùng phải bấm mù
 * cho tới khi trúng cái mình muốn, và **"tự động" trông giống hệt "sáng"** khi
 * hệ điều hành đang sáng. Họ không có cách nào biết mình đang ở chế độ nào.
 *
 * Menu hiện cả ba, đánh dấu cái đang chọn. Người dùng đọc thay vì đoán.
 *
 * Dùng lại đúng khuôn `bell-wrap`/`bell-panel` của chuông thông báo: cùng cách
 * đóng khi bấm ra ngoài, cùng cách đóng bằng Escape, cùng lớp CSS đã có.
 */
function ThemeMenu() {
  const t = useT();
  const { theme, mode, setMode } = useTheme();
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  /*
   * Biểu tượng theo MODE, không theo màu đang hiện.
   *
   * Ở chế độ tự động, biểu tượng phải nói "đang tự động" — nếu nó đổi theo màu
   * thì người dùng không phân biệt được `auto` với lựa chọn cứng, đúng vấn đề
   * mà menu này sinh ra để giải quyết.
   */
  const bieuTuong = mode === 'auto' ? 'monitor' : mode === 'dark' ? 'moon' : 'sun';

  return (
    <div className="bell-wrap" ref={boxRef}>
      <button
        className="icon-btn"
        type="button"
        aria-label={t(`nav.theme.${mode}`)}
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        data-od-id="theme-toggle"
      >
        <Icon name={bieuTuong} />
      </button>

      {open ? (
        <div className="bell-panel card theme-menu" role="menu" aria-label={t('nav.theme.label')}>
          <div className="card-body" style={{ padding: 8 }}>
            <ul className="bell-list">
              {THEME_MODES.map((m) => (
                <li key={m} className={m === mode ? 'read' : undefined}>
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={m === mode}
                    onClick={() => {
                      setMode(m);
                      setOpen(false);
                    }}
                    data-od-id={`theme-mode-${m}`}
                  >
                    <Icon
                      name={m === 'auto' ? 'monitor' : m === 'dark' ? 'moon' : 'sun'}
                      size={16}
                    />
                    <b>{t(`nav.theme.${m}`)}</b>
                    {/* Nói rõ "tự động" đang cho ra màu gì — không thì người
                        dùng phải tự suy từ màn hình. */}
                    {m === 'auto' ? (
                      <span className="small muted">{t(`nav.theme.autoNow.${theme}`)}</span>
                    ) : null}
                    {m === mode ? <Icon name="check" size={15} /> : null}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function NotificationBell() {
  const { formatRelative } = useFormat();
  const t = useT();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const listQuery = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<NotificationList>('/notifications'),
    refetchInterval: 60_000,
  });

  const markRead = useMutation({
    mutationFn: (body: { ids?: string[]; all?: boolean }) => api.post('/notifications/read', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const unread = listQuery.data?.unreadCount ?? 0;
  const items = listQuery.data?.items ?? [];

  return (
    <div className="bell-wrap" ref={boxRef}>
      <button
        className="icon-btn"
        type="button"
        aria-label={t('nav.notifications')}
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        data-od-id="notifications"
      >
        <Icon name="bell" />
        {unread > 0 ? <span className="bell-dot num">{unread > 9 ? '9+' : unread}</span> : null}
      </button>

      {open ? (
        <div className="bell-panel card" role="dialog" aria-label={t('nav.notifications')}>
          <div className="card-head">
            <span className="eyebrow grow">{t('nav.notifications')}</span>
            <button
              className="btn btn-sm btn-ghost"
              type="button"
              disabled={unread === 0 || markRead.isPending}
              onClick={() => markRead.mutate({ all: true })}
            >
              {t('notifications.markAll')}
            </button>
          </div>
          <div className="card-body" style={{ padding: 8 }}>
            {items.length === 0 ? (
              <p className="small muted" style={{ padding: '10px 8px', margin: 0 }}>
                {t('notifications.empty')}
              </p>
            ) : (
              <ul className="bell-list">
                {items.map((item) => (
                  <li
                    key={item.id}
                    className={item.readAt ? 'read' : undefined}
                    /*
                      OQ-343 — đánh dấu thông báo báo HỎNG.

                      Trước đây mọi thông báo trông y hệt nhau, nên "Nhập tên
                      miền thất bại" nằm lẫn giữa các dòng "đã xong" và trôi qua
                      mắt. Chủ dự án gặp đúng vậy ở OQ-342: bốn thông báo lỗi
                      xếp chồng mà nhìn như thông báo bình thường.

                      Bám vào ĐUÔI của `type` chứ không liệt kê từng loại: mọi
                      `*.failed` / `*.partial` sau này tự được đánh dấu, không
                      cần ai nhớ quay lại sửa danh sách.
                    */
                    data-tone={
                      /\.(failed|partial)$/.test(item.type)
                        ? 'bad'
                        : /\.(done|ok)$/.test(item.type)
                          ? 'ok'
                          : undefined
                    }
                  >
                    <button
                      type="button"
                      onClick={() => {
                        if (!item.readAt) markRead.mutate({ ids: [item.id] });
                        setOpen(false);
                        if (item.link) navigate(item.link);
                      }}
                    >
                      <b>{item.title}</b>
                      {item.body ? <span className="small muted">{item.body}</span> : null}
                      <span className="small muted num">{formatRelative(item.createdAt)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
